// ═════════════════════════════════════════════════════════════════════════════
// Shared NHCX HTTP client
//
// Same Django host as the rest of IPD billing: REACT_APP_BACKEND_ROUTE_URL
// (already includes /app/api/). Paths sit under insurance/, e.g.
//   POST {API_ROOT}insurance/coverage-eligibility/check/
//
// Org-Code / App-Mode headers, poll, and the two 422 error shapes are identical
// across coverage-eligibility, pre-auth, and claim.
// ═════════════════════════════════════════════════════════════════════════════

import axios from "axios";

// Trailing slash is in .env (`http://localhost:8000/app/api/`).
export const NHCX_BASE_URL = process.env.REACT_APP_BACKEND_ROUTE_URL || "";
export const NHCX_API_PREFIX = "insurance";

/** Absolute URL under /app/api/insurance/, e.g. nhcxUrl("/coverage-eligibility/check/"). */
export function nhcxUrl(path) {
  const root = String(NHCX_BASE_URL).replace(/\/?$/, "/");
  const trimmed = String(path).replace(/^\/+/, "");
  return `${root}${NHCX_API_PREFIX}/${trimmed}`;
}

// The lowercase session keys are the same fallbacks getAuthHeaders() uses:
// sessions written by the older login path store those instead.
export function nhcxHeaders() {
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${sessionStorage.getItem("accessToken") || ""}`,
    "Org-Code":
      sessionStorage.getItem("ORG_CODE") || sessionStorage.getItem("orgCode") || "",
    "App-Mode":
      sessionStorage.getItem("APP_MODE") || sessionStorage.getItem("appMode") || "SaaS",
  };
}

export function nhcxAppMode() {
  return nhcxHeaders()["App-Mode"];
}

/** Ack / reply endpoints take no Org-Code / App-Mode — Bearer is still required by Django. */
export function nhcxAckHeaders() {
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${sessionStorage.getItem("accessToken") || ""}`,
  };
}

function withCorrelationId(item) {
  if (!item || typeof item !== "object") return item;
  return {
    ...item,
    correlation_id: item.correlation_id || item.correlationId || item.id || "",
  };
}

/** GET /responses may be an array, a Django {data} envelope, or a map keyed by id. */
export function normalizeResponseList(payload) {
  if (Array.isArray(payload)) return payload.map(withCorrelationId);
  if (!payload || typeof payload !== "object") return [];
  for (const key of ["data", "responses", "items", "results"]) {
    const inner = payload[key];
    if (Array.isArray(inner)) return inner.map(withCorrelationId);
    if (inner && typeof inner === "object" && !Array.isArray(inner)) {
      return Object.entries(inner)
        .filter(([, v]) => v && typeof v === "object")
        .map(([id, v]) => withCorrelationId({ correlation_id: v.correlation_id || id, ...v }));
    }
  }
  return Object.entries(payload)
    .filter(([, v]) => v && typeof v === "object" && (v.use_case || v.status || v.parsed))
    .map(([id, v]) => withCorrelationId({ correlation_id: v.correlation_id || id, ...v }));
}

export async function listNhcxResponses() {
  const res = await axios.get(nhcxUrl("/responses/"), { headers: nhcxHeaders() });
  return normalizeResponseList(res.data);
}

/**
 * Poll for the payer's answer. 404 is the documented "not yet" state, so it is
 * whitelisted as a success and mapped to { pending: true } rather than thrown.
 */
export async function pollNhcxResponse(correlationId) {
  const res = await axios.get(
    nhcxUrl(`/response/${encodeURIComponent(correlationId)}/`),
    { headers: nhcxHeaders(), validateStatus: (s) => s === 200 || s === 404 }
  );
  if (res.status === 404) return { pending: true };
  return res.data;
}

// Each missing_fields entry names the override that would fix it, e.g.
//   "facility_abdm_id — facility has no ABDM ID registered. Supply
//    override_facility_abdm_id or complete ABDM facility registration."
// The patient-identifier entry names two (aadhaar OR abha) — either one satisfies it.
// patient_name / doctor_name name none: they can only be fixed in the hospital record.
const OVERRIDE_TOKEN = /override_[a-z_]+/g;

function parseMissingFields(list) {
  return (list || []).map((raw) => {
    const message = String(raw);
    return {
      message,
      overrides: [...new Set(message.match(OVERRIDE_TOKEN) || [])],
    };
  });
}

const DEFAULT_STATUS_MESSAGES = {
  400: "A required header was empty — the session may have lost its facility context.",
  404: "Admission not found.",
  500: "The NHCX service failed to build the request. Quote the API call id to support.",
  502: "The NHCX gateway is unreachable. Try again in a moment.",
  503: "The NHCX service database is unavailable. Try again in a moment.",
};

/**
 * Flatten every documented failure shape into one object the UI can render:
 *   { message, fieldErrors: [{field, message}], missing: [{message, overrides[]}] }
 *
 * 422 arrives in two shapes — a Pydantic array (field format wrong) or a Step-C
 * object (data missing from both the DB and the overrides). The object shape lists
 * everything missing at once, so one round trip reveals the full set of inputs.
 */
export function normalizeNhcxError(err, statusMessages = {}) {
  const status = err?.response?.status;
  const detail = err?.response?.data?.detail;
  const messages = { ...DEFAULT_STATUS_MESSAGES, ...statusMessages };

  if (status === 422 && Array.isArray(detail)) {
    return {
      status,
      message: "Some fields were rejected by the NHCX service.",
      fieldErrors: detail.map((d) => ({
        field: (d?.loc || []).filter((p) => p !== "body").join("."),
        message: d?.msg || "Invalid value",
      })),
      missing: [],
    };
  }

  if (status === 422 && detail && typeof detail === "object") {
    return {
      status,
      message: detail.message || "Required details are missing for this admission.",
      fieldErrors: [],
      missing: parseMissingFields(detail.missing_fields),
    };
  }

  return {
    status,
    message:
      (typeof detail === "string" && detail) ||
      messages[status] ||
      err?.message ||
      "Failed to submit the request.",
    fieldErrors: [],
    missing: [],
  };
}

// The five NHCX insurer fields have no source in the EMR — the payer master carries
// only code/name/type/active. Until they live in a backend config, they come from
// here; "custom" reveals them as inputs for an insurer not yet listed.
export const NHCX_INSURER_REGISTRY = [
  {
    key: "dummy-sandbox",
    label: "Dummy Payer (NHCX sandbox)",
    payer_code: "1000003538@hcx",
    insurer_rohini_id: "1234567",
    insurer_name: "Dummy Payer",
    insurer_phone: "+914442201234",
    insurer_email: "support@dummypayer.in",
    insurer_policy_namespace_uri: "https://dummypayer.in/policy/",
  },
];

export const NHCX_EMPTY_INSURER = {
  payer_code: "",
  insurer_rohini_id: "",
  insurer_name: "",
  insurer_phone: "",
  insurer_email: "",
  insurer_policy_namespace_uri: "",
};

export const NHCX_DUMMY_PAYER_CODE = "1000003538@hcx";
export const NHCX_SANDBOX_POLICY_NO = "100217";
export const NHCX_SANDBOX_PROVIDER_ID = "32722";
